import { Route, Routes, Navigate, useSearchParams } from "react-router-dom";
import { entities, ComposeProviders } from "byzantine";
import { initialValues } from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates";
import { useBulkTemplatePermissions } from "byzantine/src/dbbl/businessLogic/modules/bulkTemplates";
import {
  InstitutionSettingsContextProvider,
  useInstitutionSettings,
} from "../contexts/InstitutionSettingsContext";
import {
  ACH_TEMPLATES_PATH,
  areAchTemplatesAvailable,
  areDomesticWireTemplatesAvailable,
  areInternationalWireTemplatesAvailable,
  DOMESTIC_WIRE_TEMPLATES_PATH,
  INTERNATIONAL_WIRE_TEMPLATES_PATH,
  TEMPLATES_ROUTE,
} from "./constants";
import TemplatesContainer, { TAB_CONFIG } from "./TemplatesContainer";
import BulkTemplatesContainer from "./BulkTemplates/BulkTemplatesContainer";
import DomesticWireTemplatesContainer from "./DomesticWireTemplates/DomesticWireTemplatesContainer";
import { InternationalWireTemplatesContainer } from "./InternationalWireTemplates";
import TemplateDetailsScreen from "./BulkTemplates/CreateTemplate/TemplateDetailsScreen";
import TemplatePaymentsScreen from "./BulkTemplates/CreateTemplate/TemplatePaymentsScreen";
import TemplateReviewScreen from "./BulkTemplates/CreateTemplate/TemplateReviewScreen";

export { TEMPLATES_ROUTE } from "./constants";

const BulkTemplateProviders = [entities.bulkTemplates.bootstrap.Provider];

const RequireTemplateTitle = ({
  children,
  redirectTo,
}: {
  children: React.ReactNode;
  redirectTo: string;
}) => {
  const form = entities.bulkTemplates.BulkTemplateForm.useForm();
  const [searchParams] = useSearchParams();
  const { values } = form;

  if (!values.title) {
    return (
      <Navigate
        to={{ pathname: redirectTo, search: searchParams.toString() }}
        replace
      />
    );
  }

  return <>{children}</>;
};

type BulkTemplateRoutesProps = {
  limitsJson: string;
  isEditMode?: boolean;
};

const BulkTemplateRoutes = ({
  limitsJson,
  isEditMode = false,
}: BulkTemplateRoutesProps) => {
  const institution = useInstitutionSettings();
  const [searchParams] = useSearchParams();
  const permissions = useBulkTemplatePermissions();

  if (!permissions.canEdit) {
    return <Navigate to={`/${TEMPLATES_ROUTE}`} replace />;
  }
  const templateId = searchParams.get("templateId");
  const versionId = searchParams.get("versionId");

  if (isEditMode && !templateId) {
    return <Navigate to={`/${TEMPLATES_ROUTE}`} replace />;
  }

  // for the /new route, only allow no params or exactly versionId + templateId
  if (!isEditMode) {
    const hasValidCopyParams = templateId && versionId;
    const hasNoParams = searchParams.size === 0;
    if (!hasValidCopyParams && !hasNoParams) {
      return <Navigate to={`/${TEMPLATES_ROUTE}/ach/new`} replace />;
    }
  }

  if (!Object.prototype.hasOwnProperty.call(institution, "ach_next_cutoff")) {
    return null;
  }

  const limits = JSON.parse(limitsJson);
  const basePath = `/${TEMPLATES_ROUTE}/ach/${isEditMode ? "edit" : "new"}`;

  return (
    <entities.bulkTemplates.BulkTemplateForm.Provider
      initialValues={initialValues}
    >
      <Routes>
        <Route
          index
          element={<TemplateDetailsScreen isEditMode={isEditMode} />}
        />
        <Route
          path="payments"
          element={
            <RequireTemplateTitle redirectTo={basePath}>
              <TemplatePaymentsScreen limits={limits} isEditMode={isEditMode} />
            </RequireTemplateTitle>
          }
        />
        <Route
          path="review"
          element={
            <RequireTemplateTitle redirectTo={basePath}>
              <TemplateReviewScreen isEditMode={isEditMode} />
            </RequireTemplateTitle>
          }
        />
      </Routes>
    </entities.bulkTemplates.BulkTemplateForm.Provider>
  );
};

const DefaultTabRedirect = ({ features }: { features: API.Features }) => {
  const firstVisibleTab = TAB_CONFIG.find(
    (tab) => tab.enabled && tab.enabled(features)
  );
  if (firstVisibleTab) {
    return (
      <Navigate to={`/${TEMPLATES_ROUTE}/${firstVisibleTab.path}`} replace />
    );
  }
  return null;
};

type TemplatesRoutesProps = {
  limitsJson?: string;
  features: API.Features;
};

const TemplatesRoutes = ({
  limitsJson = "{}",
  features,
}: TemplatesRoutesProps) => (
  <ComposeProviders
    components={[
      ...(areAchTemplatesAvailable(features) ? BulkTemplateProviders : []),
    ]}
  >
    <Routes>
      <Route element={<TemplatesContainer features={features} />}>
        <Route index element={<DefaultTabRedirect features={features} />} />
        {areAchTemplatesAvailable(features) && (
          <>
            <Route
              path={ACH_TEMPLATES_PATH}
              element={<BulkTemplatesContainer />}
            />
            <Route
              path={`${ACH_TEMPLATES_PATH}/:templateUuid`}
              element={<BulkTemplatesContainer />}
            />
          </>
        )}
        {areDomesticWireTemplatesAvailable(features) && (
          <Route
            path={`${DOMESTIC_WIRE_TEMPLATES_PATH}/*`}
            element={
              <div className="margin--top--l">
                <DomesticWireTemplatesContainer />
              </div>
            }
          />
        )}
        {areInternationalWireTemplatesAvailable(features) && (
          <Route
            path={`${INTERNATIONAL_WIRE_TEMPLATES_PATH}/*`}
            element={
              <div className="margin--top--l">
                <InternationalWireTemplatesContainer />
              </div>
            }
          />
        )}
      </Route>
      {areAchTemplatesAvailable(features) && (
        <>
          <Route
            path="ach/new/*"
            element={
              <InstitutionSettingsContextProvider>
                <BulkTemplateRoutes limitsJson={limitsJson} />
              </InstitutionSettingsContextProvider>
            }
          />
          <Route
            path="ach/edit/*"
            element={
              <InstitutionSettingsContextProvider>
                <BulkTemplateRoutes limitsJson={limitsJson} isEditMode />
              </InstitutionSettingsContextProvider>
            }
          />
        </>
      )}
      <Route
        path="*"
        element={<Navigate to={`/${TEMPLATES_ROUTE}`} replace />}
      />
    </Routes>
  </ComposeProviders>
);

export default TemplatesRoutes;
